import type { Category } from './ItemForm';

/**
 * One header of the list and the items right under it. `depth` 0: a main category (or "Bez kategorii"), 1+: a
 * subcategory. `name` is the category's own name, `label` its whole path ("Nagłośnienie / Mikrofony").
 */
export type ItemGroup<T> = { id: string | null; name: string | null; label: string | null; depth: number; items: T[] };

/**
 * Items grouped category → subcategory (owner's remarks 14 and 4/31) in the catalog order: each main category, then
 * its subcategories one level deeper, siblings in the order the API gives them (its `sort`). A category is shown when
 * it or a category below it has items (a main category with items only in its subcategories gets an empty header).
 * Items with no known category (or one unreachable from the top, through a broken parent loop) come last as "Bez
 * kategorii". When no item has a known category the single group has no label.
 */
export function groupItems<T extends { categoryId?: string | null | undefined }>(items: T[], categories: Category[]): ItemGroup<T>[] {
  const known = new Set(categories.map((c) => c.id));
  const children = new Map<string | null, Category[]>();
  for (const c of categories) {
    const parent = c.parentId !== null && known.has(c.parentId) ? c.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), c]);
  }
  const byCategory = new Map<string, T[]>();
  const loose: T[] = [];
  for (const it of items) {
    if (it.categoryId && known.has(it.categoryId)) byCategory.set(it.categoryId, [...(byCategory.get(it.categoryId) ?? []), it]);
    else loose.push(it);
  }
  if (byCategory.size === 0) return items.length ? [{ id: null, name: null, label: null, depth: 0, items }] : [];

  const groups: ItemGroup<T>[] = [];
  /** Adds the groups of the categories under `parent`; true when any of them holds an item. */
  const walk = (parent: string | null, path: string, depth: number, seen: Set<string>): boolean => {
    let any = false;
    for (const c of children.get(parent) ?? []) {
      if (seen.has(c.id)) continue; // a broken parent chain must not loop
      const label = path ? `${path} / ${c.name}` : c.name;
      const own = byCategory.get(c.id) ?? [];
      const at = groups.length;
      const below = walk(c.id, label, depth + 1, new Set([...seen, c.id]));
      if (own.length > 0 || below) {
        groups.splice(at, 0, { id: c.id, name: c.name, label, depth, items: own });
        any = true;
      }
    }
    return any;
  };
  walk(null, '', 0, new Set());
  // a category the walk never reached (its parents form a loop) must not hide its items
  const reached = new Set(groups.map((g) => g.id));
  for (const [id, own] of byCategory) if (!reached.has(id)) loose.push(...own);
  if (loose.length) groups.push({ id: null, name: 'Bez kategorii', label: 'Bez kategorii', depth: 0, items: loose });
  return groups;
}

/** "48 szt. · 12 pozycji" (remark 4/30): pieces and items of a category with its subcategories. */
export function countText(t: { items: number; pieces: number }): string {
  const n = t.items;
  const word = n === 1 ? 'pozycja' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'pozycje' : 'pozycji';
  return `${t.pieces} szt. · ${n} ${word}`;
}

/**
 * The category whose manual order a group shows (remark 4/24): its items' common `categoryId` (null: items without
 * a category), or undefined when they do not share one (e.g. a role that cannot see categories gets one mixed group).
 */
export function orderCategoryOf<T extends { categoryId?: string | null | undefined }>(group: ItemGroup<T>): string | null | undefined {
  const first = group.items[0]?.categoryId;
  if (first === undefined || !group.items.every((i) => i.categoryId === first)) return undefined;
  return first;
}

/** The list with the items named in `ids` put in that order, on the places those items had in the list. */
export function withItemOrder<T extends { id: string }>(list: readonly T[], ids: readonly string[]): T[] {
  const wanted = new Set(ids);
  const byId = new Map(list.map((i) => [i.id, i]));
  let next = 0;
  return list.map((i) => (wanted.has(i.id) ? byId.get(ids[next++]!)! : i));
}
